import React, { useEffect, useState } from "react";
import {
  RotateCcw,
  SlidersHorizontal,
} from "lucide-react";

import RightDrawer from "../../shared/components/RightDrawer";

const DEFAULT_DEPARTMENTS = [
  { uuid: "Engineering", name: "Engineering" },
  { uuid: "Human Resources", name: "Human Resources" },
  { uuid: "Management", name: "Management" },
  { uuid: "Design & UI/UX", name: "Design & UI/UX" },
  { uuid: "Business Operations", name: "Business Operations" },
];

const DEFAULT_WORK_SCHEDULES = [
  { uuid: "General Shift (09:00 - 18:00)", schedule_name: "General Shift (09:00 - 18:00)" },
  { uuid: "Flexible Shift (10:00 - 19:00)", schedule_name: "Flexible Shift (10:00 - 19:00)" },
  { uuid: "Executive Shift (08:30 - 17:30)", schedule_name: "Executive Shift (08:30 - 17:30)" },
  { uuid: "Morning Shift (08:00 - 14:00)", schedule_name: "Morning Shift (08:00 - 14:00)" },
  { uuid: "Night Shift (22:00 - 06:00)", schedule_name: "Night Shift (22:00 - 06:00)" },
];

const DEFAULT_EMPLOYMENT_TYPES = [
  { uuid: "Full Time", type_name: "Full Time" },
  { uuid: "Part Time", type_name: "Part Time" },
  { uuid: "Contract", type_name: "Contract" },
  { uuid: "Internship", type_name: "Internship" },
  { uuid: "Temporary", type_name: "Temporary" },
  { uuid: "Consultant", type_name: "Consultant" },
  { uuid: "Freelancer", type_name: "Freelancer" },
  { uuid: "Probationary", type_name: "Probationary" },
  { uuid: "Remote Full Time", type_name: "Remote Full Time" },
];

const DEFAULT_CONTRACT_STATUSES = ["Active", "Expiring Soon", "Expired", "Draft"];
const DEFAULT_EMPLOYEE_STATUSES = ["Active", "Inactive", "On Leave", "Terminated", "Draft"];

const FilterDrawer = ({
  isOpen = false,
  open = false,
  onClose,
  filters = {},
  filterOptions = {},
  onApply,
}) => {
  const drawerOpen = isOpen || open;

  const [localFilters, setLocalFilters] =
    useState(filters);

  useEffect(() => {
    if (drawerOpen) {
      setLocalFilters({
        ...filters,
      });
    }
  }, [drawerOpen, filters]);

  const handleChange = (
    field,
    value
  ) => {
    setLocalFilters((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const handleApply = () => {
    if (typeof onApply === "function") {
      onApply(localFilters);
    }

    if (typeof onClose === "function") {
      onClose();
    }
  };

  const handleReset = () => {
    const resetValues = {
      search: "",
      department: "all",
      employmentType: "all",
      contractStatus: "all",
      workSchedule: "all",
      status: "all",
    };

    setLocalFilters(resetValues);
  };

  if (!drawerOpen) {
    return null;
  }

  const departments =
    (filterOptions.departments && filterOptions.departments.length > 0)
      ? filterOptions.departments
      : DEFAULT_DEPARTMENTS;

  const employmentTypes =
    (filterOptions.employmentTypes && filterOptions.employmentTypes.length > 0)
      ? filterOptions.employmentTypes
      : DEFAULT_EMPLOYMENT_TYPES;

  const workSchedules =
    (filterOptions.workSchedules && filterOptions.workSchedules.length > 0)
      ? filterOptions.workSchedules
      : DEFAULT_WORK_SCHEDULES;

  const contractStatuses =
    (filterOptions.contractStatuses && filterOptions.contractStatuses.length > 0)
      ? filterOptions.contractStatuses
      : DEFAULT_CONTRACT_STATUSES;

  const employeeStatuses =
    (filterOptions.employeeStatuses && filterOptions.employeeStatuses.length > 0)
      ? filterOptions.employeeStatuses
      : DEFAULT_EMPLOYEE_STATUSES;

  return (
    <RightDrawer
      open={drawerOpen}
      onClose={onClose}
      title="Filters"
      description="Refine employment records using advanced filters."
      width="420px"
      footer={
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={handleReset}
            className="
              flex
              h-10
              items-center
              gap-2
              rounded-lg
              px-3
              text-sm
              font-medium
              text-slate-500
              transition
              hover:bg-slate-50
              hover:text-slate-800
            "
          >
            <RotateCcw
              size={15}
              strokeWidth={1.8}
            />

            Reset
          </button>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="
                h-10
                rounded-lg
                border
                border-slate-200
                bg-white
                px-4
                text-sm
                font-medium
                text-slate-600
                hover:bg-slate-50
              "
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={handleApply}
              className="
                h-10
                rounded-lg
                bg-[#147db5]
                px-4
                text-sm
                font-medium
                text-white
                hover:bg-[#116d9d]
              "
            >
              Apply Filters
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-5 p-6">



        {/* Department */}

        <FilterSelect
          label="Department"
          value={
            localFilters.department ||
            "all"
          }
          onChange={(value) =>
            handleChange(
              "department",
              value
            )
          }
          options={departments.map(
            (item) => {
              const label = typeof item === "string" ? item : (item.name || item.department_name || "Department");
              const val = typeof item === "string" ? item : (item.name || item.department_name || item.uuid);
              return { value: val, label };
            }
          )}
          allLabel="All Departments"
        />

        {/* Employment Type */}

        <FilterSelect
          label="Employment Type"
          value={
            localFilters.employmentType ||
            "all"
          }
          onChange={(value) =>
            handleChange(
              "employmentType",
              value
            )
          }
          options={employmentTypes.map(
            (item) => {
              const label = typeof item === "string" ? item : (item.type_name || item.name || "Employment Type");
              const val = typeof item === "string" ? item : (item.type_name || item.name || item.uuid);
              return { value: val, label };
            }
          )}
          allLabel="All Employment Types"
        />

        {/* Contract Status */}

        <FilterSelect
          label="Contract Status"
          value={
            localFilters.contractStatus ||
            "all"
          }
          onChange={(value) =>
            handleChange(
              "contractStatus",
              value
            )
          }
          options={contractStatuses.map(
            (item) => ({
              value:
                typeof item === "string"
                  ? item
                  : item.value,
              label:
                typeof item === "string"
                  ? item
                  : item.label,
            })
          )}
          allLabel="All Contract Status"
        />

        {/* Work Schedule */}

        <FilterSelect
          label="Work Schedule"
          value={
            localFilters.workSchedule ||
            "all"
          }
          onChange={(value) =>
            handleChange(
              "workSchedule",
              value
            )
          }
          options={workSchedules.map(
            (item) => {
              const label = typeof item === "string" ? item : (item.schedule_name || item.name || "Work Schedule");
              const val = typeof item === "string" ? item : (item.schedule_name || item.name || item.uuid);
              return { value: val, label };
            }
          )}
          allLabel="All Work Schedules"
        />

        {/* Employee Status */}

        <FilterSelect
          label="Employee Status"
          value={
            localFilters.status ||
            "all"
          }
          onChange={(value) =>
            handleChange(
              "status",
              value
            )
          }
          options={employeeStatuses.map(
            (item) => ({
              value:
                typeof item === "string"
                  ? item
                  : item.value,
              label:
                typeof item === "string"
                  ? item
                  : item.label,
            })
          )}
          allLabel="All Employee Status"
        />


      </div>
    </RightDrawer>
  );
};

// Reusable Filter Select

const FilterSelect = ({
  label,
  value,
  onChange,
  options = [],
  allLabel,
}) => {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-medium text-slate-600">
        {label}
      </label>

      <select
        value={value}
        onChange={(event) =>
          onChange(event.target.value)
        }
        className="
          h-10
          w-full
          rounded-lg
          border
          border-slate-200
          bg-white
          px-3
          text-sm
          text-slate-700
          outline-none
          focus:border-[#147db5]
          focus:ring-2
          focus:ring-[#147db5]/10
        "
      >
        <option value="all">
          {allLabel}
        </option>

        {options.map((option) => (
          <option
            key={option.value}
            value={option.value}
          >
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
};

// DEFAULT EXPORT

export default FilterDrawer;